import React, { FC, useEffect } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useRouteUrl } from '../../hooks/use-route-url';
import styled from 'styled-components';
import OvButton from '../UI/atoms/OvButton';
import { useTranslation } from 'react-i18next';
import Variables from '../../design-system/variables';
import Colours from '../../design-system/colours';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import { last } from 'lodash';
import OvDateParser from '../UI/atoms/OvDateParser';
import { Alert, AlertTitle, Tab, Tabs } from '@mui/material';
import OvJournalEntryDetailPanel from '../UI/organisms/OvJournalEntryDetailPanel';
import OvUserScanList from '../UI/organisms/OvUserScanList';
import OvPrettyUserName from '../UI/atoms/OvPrettyUserName';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import queryString from 'query-string';
import { DailyDataService } from '../../services/daily-data.service';
import { JournalEntryService } from '../../services/journal-entry.service';
import { ScansService } from '../../services/scans.service';
import UserService from '../../services/user.service';
import { parseISO } from 'date-fns';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvWearableInfo from '../UI/organisms/OvWearableInfo';

const DAILY_DATA_SCAN_LIMIT = 100;

const DailyDataDetails: FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const url = useRouteUrl();
  let location = useLocation();

  const { dailyDataId }: any = useParams();
  const tabs = {
    JOURNAL_ENTRY: 'journal-entry',
    SCANS: 'scans',
    WEARABLE_INFO: 'wearable-info',
  };
  const [tab, setTab] = React.useState<string>(tabs.JOURNAL_ENTRY);
  const [userDocumentId, day]: (string | undefined)[] =
    dailyDataId?.split('_') ?? [];
  const dailyDataQueryKey = ['admin-daily-data', userDocumentId, day];

  const {
    data: selectedDailyData,
    isLoading: isDailyDataLoading,
    error: dailyDataError,
  } = useQuery({
    queryKey: dailyDataQueryKey,
    queryFn: async () => {
      const paginatedDailyData =
        await DailyDataService.loadDailyDataListByQuery(
          queryString.stringify({
            limit: 1,
            user_document_id: userDocumentId,
            day_from: day,
            day_to: day,
          })
        );

      return paginatedDailyData.docs[0] ?? null;
    },
    enabled: !!userDocumentId && !!day,
  });

  const scanQuery = queryString.stringify({
    user_document_id: userDocumentId,
    day_from: day,
    day_to: day,
    limit: DAILY_DATA_SCAN_LIMIT,
  });
  const {
    data: scanList = [],
    error: scansError,
    refetch,
  } = useQuery({
    queryKey: ['admin-scans-user', scanQuery],
    queryFn: () => ScansService.loadScans(scanQuery),
    select: (paginatedScans) => paginatedScans.docs,
    enabled: !!userDocumentId && !!day,
  });

  const {
    data: selectedUser,
    isLoading: isUserLoading,
    error: userError,
  } = useQuery({
    queryKey: ['admin-user', userDocumentId],
    queryFn: () => UserService.getUserInfoById(userDocumentId!),
    enabled: !!userDocumentId,
  });

  const updateJournalEntryMutation = useMutation({
    mutationFn: ({
      id,
      updatedProperties,
    }: {
      id: string;
      updatedProperties: any;
    }) => JournalEntryService.updateJournalEntry(id, updatedProperties),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: dailyDataQueryKey }),
  });

  const createJournalEntryMutation = useMutation({
    mutationFn: (journalEntryDay: string) =>
      JournalEntryService.createJournalEntry({
        day: journalEntryDay,
        user_id: selectedUser?.id,
      }),
    onSuccess: (journalEntry) => {
      queryClient.invalidateQueries({ queryKey: dailyDataQueryKey });

      if (selectedUser?.document_id && journalEntry.day) {
        navigate(`/daily-data/${selectedUser.document_id}_${journalEntry.day}`);
      }
    },
  });

  const isLoading =
    isUserLoading ||
    isDailyDataLoading ||
    updateJournalEntryMutation.isPending ||
    createJournalEntryMutation.isPending;
  const error =
    dailyDataError ??
    scansError ??
    userError ??
    updateJournalEntryMutation.error ??
    createJournalEntryMutation.error;

  useEffect(() => {
    const setActiveTabFromUrl = () => {
      const pathName = last(location.pathname.split('/'));

      if (
        pathName === tabs.SCANS ||
        pathName === tabs.JOURNAL_ENTRY ||
        pathName === tabs.WEARABLE_INFO
      ) {
        setTab(pathName);
      }
    };

    setActiveTabFromUrl();
  }, [
    tabs.SCANS,
    tabs.JOURNAL_ENTRY,
    tabs.WEARABLE_INFO,
    navigate,
    url,
    location.pathname,
  ]);

  const handleTabChange = (event: any, newTab: string) => {
    if (newTab !== null) {
      setTab(newTab);
    }
  };

  const onSaveEditedJournalEntryField = (editObject: any) => {
    if (selectedDailyData?.journal_entry_id) {
      updateJournalEntryMutation.mutate({
        id: selectedDailyData.journal_entry_id,
        updatedProperties: {
          ...editObject,
        },
      });
    }
  };

  const createNewJournalEntry = () => {
    if (selectedDailyData?.day) {
      createJournalEntryMutation.mutate(selectedDailyData.day);
    }
  };

  return (
    <>
      <DetailPageWrapper>
        <Header>
          <InfoWrapper>
            <OvBackButton label={t('common.actions.back')} />
            <StyledLink to={`/users/${selectedDailyData?.user_id}`}>
              <PrettyUserName>
                <OvPrettyUserName user={selectedUser} />
              </PrettyUserName>
            </StyledLink>
            <DateInfo>
              <Separator>-</Separator>
              {selectedDailyData && (
                <OvDateParser
                  date={parseISO(selectedDailyData.day as string)}
                  format={'dddd, MMMM DD, YYYY'}
                />
              )}
            </DateInfo>
          </InfoWrapper>
          <UserActionWrapper>
            {selectedDailyData?.journal_entry_id === null && (
              <StyledOvButton onClick={createNewJournalEntry}>
                {t('dailyDataDetails.journalEntry.createJournalEntry')}
              </StyledOvButton>
            )}
          </UserActionWrapper>
        </Header>

        {error && <OvErrorMessage message={error.message} />}

        {selectedDailyData?.journal_entry?.is_demo_journal_entry && (
          <StyledAlert severity="warning">
            <AlertTitle>
              {t('dailyDataDetails.journalEntry.demoAlertTitle')}
            </AlertTitle>
            {t('dailyDataDetails.journalEntry.demoAlertDesc')}
          </StyledAlert>
        )}

        <TabContainer>
          <Tabs onChange={handleTabChange} value={tab}>
            <Tab
              value={tabs.JOURNAL_ENTRY}
              label={t('dailyDataDetails.tabs.journalEntry')}
              component={Link}
              to={`${url}/${tabs.JOURNAL_ENTRY}`}
              replace
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value={tabs.SCANS}
              label={t('dailyDataDetails.tabs.scans')}
              component={Link}
              to={`${url}/${tabs.SCANS}`}
              replace
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value={tabs.WEARABLE_INFO}
              label={t('dailyDataDetails.tabs.wearableInfo')}
              component={Link}
              to={`${url}/${tabs.WEARABLE_INFO}`}
              replace
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
          </Tabs>
        </TabContainer>

        <Routes>
          <Route
            path={`${tabs.JOURNAL_ENTRY}/*`}
            element={
              <OvJournalEntryDetailPanel
                journalEntry={selectedDailyData?.journal_entry}
                onSaveEditedJournalField={onSaveEditedJournalEntryField}
              />
            }
          />
          <Route
            path={`${tabs.SCANS}/*`}
            element={
              <OvUserScanList
                scans={scanList}
                showDetailsAction={true}
                selectedUserId={userDocumentId}
                selectedDay={day}
                showOperation={true}
                refetch={refetch}
              />
            }
          />
          <Route
            path={`${tabs.WEARABLE_INFO}/*`}
            element={
              <OvWearableInfo spikeInfo={selectedDailyData?.spike_info} />
            }
          />
          <Route
            index
            element={<Navigate to={`${url}/${tabs.JOURNAL_ENTRY}`} replace />}
          />
        </Routes>
      </DetailPageWrapper>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </>
  );
};

export default DailyDataDetails;

const DetailPageWrapper = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.SMALL};
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  font-weight: bold;
`;

const UserActionWrapper = styled.div``;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const PrettyUserName = styled.h6`
  margin: 0;
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.5rem;
`;

const DateInfo = styled.span`
  font-weight: normal;
  font-size: 1rem;
  line-height: 1.5rem;
`;

const Separator = styled.span`
  margin: 0 0.75rem;
`;

const TabContainer = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  && {
    .MuiTabs-indicator {
      background-color: ${Colours.OV_BASE};
    }
  }
`;

const StyledLink = styled(Link)`
  && {
    text-decoration: none;
    color: ${Colours.OV_BASE};

    &:hover {
      color: ${Colours.OV_BASE_HOVER};
    }
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    display: flex;
    align-items: center;
    text-transform: none;
    padding: 2px 10px;
    padding: 2px 0.625rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.5rem;
    background-color: ${Colours.OV_BASE};
    &:last-child {
      margin-right: 0;
    }
  }
`;

const StyledAlert = styled(Alert)`
  && {
    margin: 0.25rem 0 0.5rem 0;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_YELLOW};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
